import React, { useState, useEffect } from 'react';
import { FaPlus, FaTrash, FaEdit } from 'react-icons/fa';
import TranslationButton from '../../ui/TranslationButton/TranslationButton';
import translationService from '../../../services/translationService';
import ImageUpload from '../../common/ImageUpload';
import styles from './EnhancedBlogFormWithTranslation.module.css';

export default function EnhancedBlogFormWithTranslation({ 
  onSubmit, 
  onCancel,
  initialData = null, 
  loading = false 
}) {
  const [blogData, setBlogData] = useState({
    title: initialData?.title || '',
    summary: initialData?.summary || '',
    content: initialData?.content || '',
    imageUrl: initialData?.imageUrl || '',
    author: initialData?.author || ''
  });

  // Translation state
  const [translatedData, setTranslatedData] = useState({
    title: { ar: '', en: '', tr: '' },
    summary: { ar: '', en: '', tr: '' },
    content: { ar: '', en: '', tr: '' }
  });

  // Update form data when initialData changes (for editing)
  useEffect(() => {
    if (initialData) {
      setBlogData({
        title: initialData.title || '',
        summary: initialData.summary || '',
        content: initialData.content || '',
        imageUrl: initialData.imageUrl || '',
        author: initialData.author || ''
      });

      // Load existing translations if available
      if (initialData.translations) {
        setTranslatedData(initialData.translations);
      }
    } else {
      // Reset form when not editing
      setBlogData({
        title: '',
        summary: '',
        content: '',
        imageUrl: '',
        author: ''
      });
      setTranslatedData({
        title: { ar: '', en: '', tr: '' },
        summary: { ar: '', en: '', tr: '' },
        content: { ar: '', en: '', tr: '' }
      });
    }
  }, [initialData]);

  const handleInputChange = (field, value) => {
    setBlogData(prev => ({
      ...prev,
      [field]: value
    }));

    // Update translated data for translatable fields
    if (['title', 'summary', 'content'].includes(field)) {
      setTranslatedData(prev => ({
        ...prev,
        [field]: {
          ...prev[field],
          ar: value
        }
      }));
    }
  };

  // Handle translation for a specific field
  const handleTranslateField = (fieldName, translations) => {
    setTranslatedData(prev => ({
      ...prev,
      [fieldName]: {
        ...prev[fieldName],
        en: translations.en,
        tr: translations.tr
      }
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Combine blog data with translations
    const finalBlogData = {
      ...blogData,
      translations: translatedData
    };

    onSubmit(finalBlogData);
  };

  return (
    <div className={styles.container}>
      <form onSubmit={handleSubmit} className={styles.form}>
        <div className={styles.formHeader}>
          <h2>{initialData ? 'Edit Blog Post' : 'Add New Blog Post'}</h2>
          <p>Create a new blog post with translations for multiple languages</p>
        </div>

        {/* Blog Information */}
        <div className={styles.section}>
          <h3>Blog Information</h3>
          
          <div className={styles.formGroup}>
            <label>Blog Title *</label>
            <div className={styles.inputWithTranslation}>
              <input
                type="text"
                value={blogData.title}
                onChange={(e) => handleInputChange('title', e.target.value)}
                placeholder="Enter blog title"
                required
              />
              <TranslationButton
                text={blogData.title}
                onTranslate={(translations) => handleTranslateField('title', translations)}
                size="small"
              />
            </div>
            {translatedData.title.en && (
              <div className={styles.translationResults}>
                <div className={styles.translationResult}>
                  <span className={styles.translationLabel}>EN:</span>
                  <span className={styles.translationText}>{translatedData.title.en}</span>
                </div>
                <div className={styles.translationResult}>
                  <span className={styles.translationLabel}>TR:</span>
                  <span className={styles.translationText}>{translatedData.title.tr}</span>
                </div>
              </div>
            )}
          </div>

          <div className={styles.formGroup}>
            <label>Blog Summary *</label>
            <div className={styles.inputWithTranslation}>
              <textarea
                value={blogData.summary}
                onChange={(e) => handleInputChange('summary', e.target.value)}
                placeholder="Enter a brief summary of the blog post"
                rows="3"
                required
              />
              <TranslationButton
                text={blogData.summary}
                onTranslate={(translations) => handleTranslateField('summary', translations)}
                size="small"
              />
            </div>
            {translatedData.summary.en && (
              <div className={styles.translationResults}>
                <div className={styles.translationResult}>
                  <span className={styles.translationLabel}>EN:</span>
                  <span className={styles.translationText}>{translatedData.summary.en}</span>
                </div>
                <div className={styles.translationResult}>
                  <span className={styles.translationLabel}>TR:</span>
                  <span className={styles.translationText}>{translatedData.summary.tr}</span>
                </div>
              </div>
            )}
          </div>

          <div className={styles.formGroup}>
            <label>Blog Content *</label>
            <div className={styles.inputWithTranslation}>
              <textarea
                value={blogData.content}
                onChange={(e) => handleInputChange('content', e.target.value)}
                placeholder="Write your blog post content here..."
                rows="10"
                required
              />
              <TranslationButton
                text={blogData.content}
                onTranslate={(translations) => handleTranslateField('content', translations)}
                size="small"
              />
            </div>
            {translatedData.content.en && (
              <div className={styles.translationResults}>
                <div className={styles.translationResult}>
                  <span className={styles.translationLabel}>EN:</span>
                  <span className={styles.translationText}>{translatedData.content.en}</span>
                </div>
                <div className={styles.translationResult}>
                  <span className={styles.translationLabel}>TR:</span>
                  <span className={styles.translationText}>{translatedData.content.tr}</span>
                </div>
              </div>
            )}
          </div>

          <div className={styles.formGroup}>
            <label>Author Name *</label>
            <input
              type="text"
              value={blogData.author}
              onChange={(e) => handleInputChange('author', e.target.value)}
              placeholder="Enter author name"
              required
            />
          </div>

          <div className={styles.formGroup}>
            <label>Blog Image</label>
            <ImageUpload
              label="Blog Image"
              initialUrl={blogData.imageUrl}
              onUpload={url => handleInputChange('imageUrl', url)}
            />
          </div>
        </div>

        {/* Translation Summary */}
        <div className={styles.section}>
          <h3>Translation Summary</h3>
          <div className={styles.translationSummary}>
            <div className={styles.translationStatus}>
              <h4>Translation Status</h4>
              <div className={styles.statusGrid}>
                <div className={styles.statusItem}>
                  <span className={styles.statusLabel}>Title:</span>
                  <span className={`${styles.statusIndicator} ${translatedData.title.en ? styles.completed : styles.pending}`}>
                    {translatedData.title.en ? '✓ Translated' : '⏳ Pending'}
                  </span>
                </div>
                <div className={styles.statusItem}>
                  <span className={styles.statusLabel}>Summary:</span>
                  <span className={`${styles.statusIndicator} ${translatedData.summary.en ? styles.completed : styles.pending}`}>
                    {translatedData.summary.en ? '✓ Translated' : '⏳ Pending'}
                  </span>
                </div>
                <div className={styles.statusItem}>
                  <span className={styles.statusLabel}>Content:</span>
                  <span className={`${styles.statusIndicator} ${translatedData.content.en ? styles.completed : styles.pending}`}>
                    {translatedData.content.en ? '✓ Translated' : '⏳ Pending'}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Form Actions */}
        <div className={styles.formActions}>
          <button type="submit" disabled={loading} className={styles.submitBtn}>
            {loading ? 'Saving...' : (initialData ? 'Update Blog Post' : 'Add Blog Post')}
          </button>
          <button type="button" onClick={onCancel} className={styles.cancelBtn}>
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
} 